// Ported from vouchers' brand-color-field: the swatch, the role name, and
// the hex value live inside one bordered box (the role is part of the
// picker, not a label beside it). The swatch opens the native color picker;
// the hex is editable. Validation messages come from the surrounding form
// Field, which renders no label of its own.

import { cn } from "@/lib/utils";

const HEX = /^#[0-9A-Fa-f]{6}$/;
const NEUTRAL_SWATCH = "#888888";

interface BrandColorFieldProps {
    /** Role caption shown inside the control (e.g. "Background", "Text"). */
    caption: string;
    value: string;
    onChange: (hex: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
}

export function BrandColorField({ caption, value, onChange, onBlur, disabled }: BrandColorFieldProps) {
    const swatch = HEX.test(value) ? value.toLowerCase() : NEUTRAL_SWATCH;
    return (
        <div
            className={cn(
                "flex items-center gap-2.5 rounded-md border border-input bg-background py-1 pl-1 pr-2.5",
                "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30",
                disabled && "opacity-50",
            )}
        >
            <span className="relative size-8 shrink-0 overflow-hidden rounded" style={{ backgroundColor: swatch }}>
                <input
                    type="color"
                    aria-label={`${caption} color picker`}
                    disabled={disabled}
                    value={swatch}
                    onChange={(e) => onChange(e.target.value.toUpperCase())}
                    className="absolute inset-[-4px] cursor-pointer opacity-0 disabled:cursor-not-allowed"
                />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-[11px] leading-tight text-muted-foreground">{caption}</span>
                <input
                    aria-label={caption}
                    value={value}
                    disabled={disabled}
                    spellCheck={false}
                    maxLength={7}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    className="w-full bg-transparent font-mono text-xs uppercase leading-tight text-foreground outline-none"
                />
            </div>
        </div>
    );
}
